/**
 * FIFO ring buffer with a hard cap.
 *
 * When the buffer is full, the oldest entry is evicted before the new one
 * is written (FIFO overflow). All operations are O(1) amortised.
 *
 * Constants exposed for tests and dependent modules:
 *   CAP             — maximum number of entries (500)
 *   EntryType       — discriminated union of entry kinds
 *   Entry           — shape of every buffer record
 *   ringBuffer      — singleton instance (push / getAll / clear)
 */

export type EntryType =
  | 'log'
  | 'warn'
  | 'error'
  | 'http'
  | 'global-error'
  | 'rejection';

export interface Entry {
  /** Unix-ms timestamp recorded at capture time */
  t: number;
  type: EntryType;
  /** JSON-serialized payload (already sanitised by safeSerialize) */
  payload: string;
}

/** Hard cap: maximum number of entries the buffer retains. */
export const CAP = 500;

/**
 * Internal ring-buffer implementation.
 *
 * Uses a fixed-length array plus a head pointer to achieve O(1) push
 * without ever re-allocating. `getAll()` reconstructs the logical order
 * from the head pointer.
 */
class RingBuffer {
  private readonly _buf: Array<Entry | undefined>;
  private _head = 0; // index of the next write slot
  private _size = 0; // number of valid entries (≤ CAP)

  constructor(private readonly cap: number) {
    this._buf = new Array<Entry | undefined>(cap).fill(undefined);
  }

  push(entry: Entry): void {
    this._buf[this._head] = entry;
    this._head = (this._head + 1) % this.cap;
    if (this._size < this.cap) {
      this._size++;
    }
    // When the buffer is full, _head has already advanced past the oldest slot,
    // effectively overwriting it — no explicit eviction step needed.
  }

  /**
   * Returns entries in insertion order (oldest → newest).
   * Allocates a new array on every call; callers should cache if needed.
   */
  getAll(): Entry[] {
    if (this._size === 0) return [];

    const result: Entry[] = new Array<Entry>(this._size);
    const startSlot =
      this._size < this.cap
        ? 0 // buffer not yet full: logical start is slot 0
        : this._head; // buffer full: oldest is the slot _head is about to overwrite

    for (let i = 0; i < this._size; i++) {
      result[i] = this._buf[(startSlot + i) % this.cap] as Entry;
    }
    return result;
  }

  clear(): void {
    this._buf.fill(undefined);
    this._head = 0;
    this._size = 0;
  }
}

/** Singleton ring buffer used by the in-app console capture pipeline. */
export const ringBuffer = new RingBuffer(CAP);
